import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getCRMSegmentFilterFields, getCRMTags } from '../api';
import { getCompanyEmployees } from '../../api';
import { PlusIcon } from '../../components/Icons';
import trashIcon from '../../assets/trashIcon.png';
import { CustomSelect } from '@/components';
import toast from 'react-hot-toast';

const PREDEFINED_SELECT_FIELDS = new Set([
  'consent_status',
  'tags',
  'pipeline_stage',
  'source_type',
  'assigned_to_employee',
]);

/** Fields that page + search via dedicated list APIs (not only filter-fields). */
const ASYNC_PAGED_SELECT_FIELDS = new Set(['tags', 'assigned_to_employee']);

const EMPTY_SELECT_HINTS = {
  tags: 'Create a tag first',
  pipeline_stage: 'Create a stage first',
  assigned_to_employee: 'No employees available',
  consent_status: 'No options available',
  source_type: 'No options available',
};

function getNextPageParam(lastPage) {
  if (!lastPage?.next) return undefined;
  try {
    const url = new URL(lastPage.next);
    return url.searchParams.get('page') || undefined;
  } catch {
    const match = String(lastPage.next).match(/[?&]page=(\d+)/);
    return match ? match[1] : undefined;
  }
}

function flattenPageResults(data) {
  return (
    data?.pages?.flatMap((page) =>
      Array.isArray(page) ? page : page?.results || [],
    ) || []
  );
}

function AsyncPaginatedValueSelect({
  fieldKey,
  companyId,
  value,
  onChange,
  seedOptions = [],
  emptyMessage,
  disabled,
}) {
  const [search, setSearch] = useState('');

  const isTags = fieldKey === 'tags';

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: isTags
        ? ['crm-segment-tags', companyId, search]
        : ['crm-segment-employees', companyId, search],
      queryFn: async ({ pageParam = 1 }) => {
        const params = search
          ? { search, page: pageParam }
          : { page: pageParam };
        if (isTags) return getCRMTags(companyId, params);
        return getCompanyEmployees(companyId, params);
      },
      getNextPageParam,
      enabled: !!companyId && ASYNC_PAGED_SELECT_FIELDS.has(fieldKey),
    });

  const pageOptions = useMemo(() => {
    const rows = flattenPageResults(data);
    if (isTags) {
      return rows.map((t) => ({
        label: t.name,
        value: t.id,
      }));
    }
    return rows.map((e) => ({
      label:
        e.full_name ||
        e.name ||
        [e.first_name, e.last_name].filter(Boolean).join(' ') ||
        e.email ||
        `Employee ${e.id}`,
      value: e.id,
    }));
  }, [data, isTags]);

  // Keep labels for already-selected values even if not on current page/search.
  const options = useMemo(() => {
    const byValue = new Map();
    seedOptions.forEach((o) => byValue.set(String(o.value), o));
    pageOptions.forEach((o) => byValue.set(String(o.value), o));
    const selected = normalizeSelectFilterValue(value);
    // Prefer page order for browse list; append missing selected from seed.
    const merged = [...pageOptions];
    selected.forEach((v) => {
      if (!merged.some((o) => String(o.value) === String(v))) {
        const seed = byValue.get(String(v));
        if (seed) merged.push(seed);
      }
    });
    return merged;
  }, [pageOptions, seedOptions, value]);

  const handleSearchChange = useCallback((q) => {
    setSearch(q);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <CustomSelect
      multiple
      searchable
      disabled={disabled}
      isLoading={isLoading}
      value={normalizeSelectFilterValue(value)}
      onChange={onChange}
      options={options}
      emptyMessage={emptyMessage}
      placeholder="Select values..."
      searchPlaceholder="Search..."
      className="w-full h-full"
      textSizeClass="text-sm"
      onSearchChange={handleSearchChange}
      onLoadMore={handleLoadMore}
      hasMore={!!hasNextPage}
      isFetchingMore={isFetchingNextPage}
    />
  );
}

const renderOperatorLabel = (symbol, text) => {
  const isTextSymbol = symbol.length > 2;
  return (
    <div className="flex items-center gap-3">
      <div
        className={`flex-shrink-0 flex items-center justify-center bg-surface-mint border border-border-default rounded font-mono text-primary font-bold uppercase tracking-wider ${
          isTextSymbol ? 'px-2 py-1 text-xs' : 'w-7 h-7 text-base'
        }`}
      >
        {symbol}
      </div>
      <span className="whitespace-nowrap">{text}</span>
    </div>
  );
};

const OPERATORS_CONFIG = {
  equals: {
    value: 'equals',
    label: renderOperatorLabel('=', 'Exactly Equals'),
  },
  contains: { value: 'contains', label: renderOperatorLabel('=', 'Contains') },
  in: { value: 'in', label: renderOperatorLabel('∈', 'In List') },
  gte: {
    value: 'gte',
    label: renderOperatorLabel('≥', 'Greater Than or Equal'),
  },
  lte: { value: 'lte', label: renderOperatorLabel('≤', 'Less Than or Equal') },
  has_type_within_days: {
    value: 'has_type_within_days',
    label: renderOperatorLabel('⏱', 'Has Type Within Days'),
  },
  not_contains: {
    value: 'not_contains',
    label: renderOperatorLabel('≠', 'Does Not Contain'),
  },
  starts_with: {
    value: 'starts_with',
    label: renderOperatorLabel('↑', 'Starts With'),
  },
  ends_with: {
    value: 'ends_with',
    label: renderOperatorLabel('↓', 'Ends With'),
  },
};

export function isSelectFilterField(fieldKey, fieldMeta) {
  if (fieldMeta?.type === 'select') return true;
  return PREDEFINED_SELECT_FIELDS.has(fieldKey);
}

export function normalizeSelectFilterValue(value) {
  if (Array.isArray(value)) return value;
  if (value === '' || value == null) return [];
  return [value];
}

export function getSelectEmptyHint(fieldKey) {
  return EMPTY_SELECT_HINTS[fieldKey] || 'No options available';
}

export function SegmentRuleBuilder({
  rules,
  setRules,
  companyId,
  matchType,
  setMatchType,
  isReadOnly = false,
}) {
  const { data: fieldsResponse } = useQuery({
    queryKey: ['crm-segment-filter-fields', companyId],
    queryFn: () => getCRMSegmentFilterFields(companyId),
    enabled: !!companyId,
  });

  const allFields = useMemo(() => {
    const fieldsData = fieldsResponse?.results || fieldsResponse || [];
    return (Array.isArray(fieldsData) ? fieldsData : []).map((f) => ({
      value: f.value,
      label: f.label,
      type: f.type || 'string',
      operators: f.operators || [],
      values: Array.isArray(f.values)
        ? f.values
        : Array.isArray(f.options)
          ? f.options
          : [],
    }));
  }, [fieldsResponse]);

  const fieldsByValue = useMemo(() => {
    const map = new Map();
    allFields.forEach((f) => map.set(f.value, f));
    return map;
  }, [allFields]);

  // Normalize saved select conditions once filter-fields know types (drop legacy operators, wrap scalar values).
  const needsSelectNormalize = useMemo(() => {
    if (!allFields.length) return false;
    return rules.some((r) => {
      if (!r.field) return false;
      const meta = fieldsByValue.get(r.field);
      if (!isSelectFilterField(r.field, meta)) return false;
      return (
        Boolean(r.operator) || r.type !== 'select' || !Array.isArray(r.value)
      );
    });
  }, [allFields.length, fieldsByValue, rules]);

  useEffect(() => {
    if (!needsSelectNormalize) return;

    setRules((prev) =>
      prev.map((r) => {
        if (!r.field) return r;
        const meta = fieldsByValue.get(r.field);
        if (!isSelectFilterField(r.field, meta)) return r;
        return {
          ...r,
          type: 'select',
          operator: '',
          value: normalizeSelectFilterValue(r.value),
        };
      }),
    );
  }, [needsSelectNormalize, fieldsByValue, setRules]);

  const getOperatorsForField = (fieldValue) => {
    if (!fieldValue) return [];
    const field = fieldsByValue.get(fieldValue);
    const operatorKeys = field?.operators || [];
    return operatorKeys.map(
      (key) =>
        OPERATORS_CONFIG[key] || {
          value: key,
          label: renderOperatorLabel('?', key.replace(/_/g, ' ')),
        },
    );
  };

  const handleAddConditionClick = () => {
    setRules([
      ...rules,
      { id: Date.now().toString(), field: '', operator: '', value: '' },
    ]);
  };

  const removeRule = (id) => {
    setRules(rules.filter((r) => r.id !== id));
  };

  const updateRule = (id, field, value) => {
    setRules(
      rules.map((r) => {
        if (r.id === id) {
          return { ...r, [field]: value };
        }
        return r;
      }),
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <h3 className="text-base font-bold text-text-primary tracking-wide">
          Filter Rules
        </h3>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => !isReadOnly && setMatchType('and')}
            className={`h-10 px-5 flex items-center justify-center text-sm font-bold rounded-xl border transition-all ${
              matchType === 'and'
                ? 'bg-primary border-primary text-white'
                : 'bg-white border-border-default text-text-secondary hover:text-text-primary'
            } ${isReadOnly ? 'cursor-default' : ''}`}
          >
            Match All
          </button>
          <button
            type="button"
            onClick={() => !isReadOnly && setMatchType('or')}
            className={`h-10 px-5 flex items-center justify-center text-sm font-bold rounded-xl border transition-all ${
              matchType === 'or'
                ? 'bg-primary border-primary text-white'
                : 'bg-white border-border-default text-text-secondary hover:text-text-primary'
            } ${isReadOnly ? 'cursor-default' : ''}`}
          >
            Match Any
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {rules.map((rule, index) => {
          const fieldMeta = fieldsByValue.get(rule.field);
          const isSelect = isSelectFilterField(rule.field, fieldMeta);
          const selectOptions = fieldMeta?.values || [];
          const emptyHint = getSelectEmptyHint(rule.field);

          return (
            <Fragment key={rule.id}>
              <div className="p-3 sm:p-4 rounded-xl border border-border-default bg-white flex flex-col gap-3 relative group hover:border-primary/30 transition-all duration-300">
                <div className="flex justify-between items-center mb-0.5">
                  <span className="text-sm font-bold text-primary">
                    Condition {index + 1}
                  </span>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => removeRule(rule.id)}
                      className="hover:opacity-80 p-1.5 rounded-full transition-opacity"
                      title="Remove Rule"
                    >
                      <img
                        src={trashIcon}
                        alt=""
                        className="w-4 h-4 object-contain"
                      />
                    </button>
                  )}
                </div>

                <div
                  className={`grid grid-cols-1 gap-3 ${
                    isSelect ? 'md:grid-cols-2' : 'md:grid-cols-3'
                  }`}
                >
                  <div id={`field-wrapper-${rule.id}`} className="w-full h-10">
                    <CustomSelect
                      disabled={isReadOnly}
                      value={rule.field}
                      onChange={(val) => {
                        const cfg = fieldsByValue.get(val);
                        const nextIsSelect = isSelectFilterField(val, cfg);
                        setRules(
                          rules.map((r) =>
                            r.id === rule.id
                              ? {
                                  ...r,
                                  field: val,
                                  type: cfg?.type || 'string',
                                  operator: '',
                                  value: nextIsSelect ? [] : '',
                                }
                              : r,
                          ),
                        );
                      }}
                      options={allFields}
                      placeholder="Select Field..."
                      className="w-full h-full"
                      textSizeClass="text-sm"
                    />
                  </div>

                  {!isSelect && (
                    <div
                      className="w-full h-10"
                      onClickCapture={(e) => {
                        if (!rule.field) {
                          e.stopPropagation();
                          e.preventDefault();
                          toast.error('Please select a field first');
                          const fieldBtn = document.querySelector(
                            `#field-wrapper-${rule.id} button`,
                          );
                          if (fieldBtn) fieldBtn.click();
                        }
                      }}
                    >
                      <CustomSelect
                        disabled={isReadOnly}
                        value={rule.operator}
                        onChange={(val) => updateRule(rule.id, 'operator', val)}
                        options={getOperatorsForField(rule.field)}
                        dropdownWidth={260}
                        placeholder="Select Operator..."
                        className="w-full h-full"
                        textSizeClass="text-sm"
                      />
                    </div>
                  )}

                  {isSelect ? (
                    ASYNC_PAGED_SELECT_FIELDS.has(rule.field) ? (
                      <div className="w-full h-10">
                        <AsyncPaginatedValueSelect
                          fieldKey={rule.field}
                          companyId={companyId}
                          value={rule.value}
                          onChange={(val) => updateRule(rule.id, 'value', val)}
                          seedOptions={selectOptions}
                          emptyMessage={emptyHint}
                          disabled={isReadOnly}
                        />
                      </div>
                    ) : selectOptions.length === 0 ? (
                      <div className="w-full min-h-10 px-3 py-2 rounded-lg border border-dashed border-border-default bg-form-background-light flex items-center">
                        <p className="text-sm text-text-muted">{emptyHint}</p>
                      </div>
                    ) : (
                      <div className="w-full h-10">
                        <CustomSelect
                          multiple
                          searchable
                          disabled={isReadOnly}
                          value={normalizeSelectFilterValue(rule.value)}
                          onChange={(val) => updateRule(rule.id, 'value', val)}
                          options={selectOptions}
                          emptyMessage={emptyHint}
                          placeholder="Select values..."
                          searchPlaceholder="Search..."
                          className="w-full h-full"
                          textSizeClass="text-sm"
                        />
                      </div>
                    )
                  ) : rule.field === 'activity' &&
                    rule.operator === 'has_type_within_days' ? (
                    <div className="flex gap-2 w-full">
                      <input
                        type="text"
                        placeholder="Activity Type..."
                        value={rule.value ? rule.value.split(':')[0] || '' : ''}
                        onChange={(e) => {
                          const currentDays = rule.value
                            ? rule.value.split(':')[1] || ''
                            : '';
                          updateRule(
                            rule.id,
                            'value',
                            `${e.target.value}:${currentDays}`,
                          );
                        }}
                        disabled={isReadOnly}
                        className={`w-1/2 h-10 px-3 rounded-lg bg-white border border-border-default text-text-primary text-sm placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="number"
                        placeholder="Days..."
                        value={rule.value ? rule.value.split(':')[1] || '' : ''}
                        onChange={(e) => {
                          const currentType = rule.value
                            ? rule.value.split(':')[0] || ''
                            : '';
                          updateRule(
                            rule.id,
                            'value',
                            `${currentType}:${e.target.value}`,
                          );
                        }}
                        disabled={isReadOnly}
                        className={`w-1/2 h-10 px-3 rounded-lg bg-white border border-border-default text-text-primary text-sm placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                      />
                    </div>
                  ) : (
                    <input
                      type="text"
                      placeholder="Enter value..."
                      value={
                        typeof rule.value === 'string' ||
                        typeof rule.value === 'number'
                          ? rule.value
                          : ''
                      }
                      onChange={(e) =>
                        updateRule(rule.id, 'value', e.target.value)
                      }
                      disabled={isReadOnly}
                      className={`w-full h-10 px-3 rounded-lg bg-white border border-border-default text-text-primary text-sm placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                    />
                  )}
                </div>
              </div>
              {index < rules.length - 1 && (
                <div className="self-center bg-surface-mint border border-orange-active-border rounded-[10px] px-3 py-1.5 text-xs font-bold text-primary shadow-sm capitalize flex items-center justify-center min-w-[50px]">
                  <span>{matchType === 'and' ? 'And' : 'Or'}</span>
                </div>
              )}
            </Fragment>
          );
        })}

        {rules.length === 0 && (
          <div className="py-10 text-center border border-dashed border-border-default rounded-xl bg-white">
            <p className="text-base text-text-secondary">
              No rules defined yet.
            </p>
            <p className="text-sm text-text-muted mt-1">
              Add conditions to start filtering your contacts.
            </p>
          </div>
        )}
      </div>

      {!isReadOnly && (
        <button
          type="button"
          onClick={handleAddConditionClick}
          className="w-full mt-1 py-3 rounded-xl border border-dashed border-border-default text-text-secondary hover:text-primary hover:border-primary/50 hover:bg-surface-mint transition-all duration-300 flex items-center justify-center gap-2 text-sm font-semibold group"
        >
          Add Another Condition
          <PlusIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
        </button>
      )}
    </div>
  );
}
